import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  EmojiEvents as RankingIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  School as LearningIcon,
  WorkspacePremium as CertificateIcon,
  Description as ReportsIcon,
  Visibility as ViewIcon,
  Compare as CompareIcon,
  AutoAwesome as RecommendationsIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import PlayerSidebar from '@components/layout/PlayerSidebar';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';

const DRAWER_WIDTH = 280;

export interface ReportRow {
  id: string;
  type: 'training' | 'ranking';
  sport: string;
  period: string;
  submittedAt: string;
  evaluatedBy: string;
  scoreLabel: string;
  categoryLabel?: string;
  reportId?: string;
  /** Training only: 'high-level' | 'low-level' | 'quick' | 'advanced' */
  assessmentType?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  'self-evaluation': 'Self Evaluation',
  'coach-academy': 'Coach/Academy Evaluation',
  'practical-test': 'Board Exam',
};

/** If content looks like raw JSON or markdown-wrapped JSON, parse and return readable text. */
function toReadableRecommendations(content: string): string {
  if (!content || typeof content !== 'string') return content;
  const trimmed = content.trim();
  if (!trimmed.includes('"recommendations"') && !trimmed.includes('```json')) return content;
  try {
    let jsonStr = trimmed.replace(/^[^`]*```json\s*/i, '').replace(/\s*```[^`]*$/i, '').trim();
    const start = jsonStr.indexOf('{');
    if (start !== -1) {
      let depth = 0;
      for (let i = start; i < jsonStr.length; i++) {
        if (jsonStr[i] === '{') depth++;
        else if (jsonStr[i] === '}') { depth--; if (depth === 0) { jsonStr = jsonStr.slice(start, i + 1); break; } }
      }
    }
    const parsed = JSON.parse(jsonStr);
    const rec = Array.isArray(parsed.recommendations) ? parsed.recommendations : [];
    const plan = typeof parsed.trainingPlan === 'string' ? parsed.trainingPlan : (parsed.training_plan || '');
    return (rec.length ? rec.map((r: string, i: number) => `${i + 1}. ${r}`).join('\n') + '\n\n' : '') + (plan ? `Practice / Plan:\n${plan}` : '');
  } catch {
    return content;
  }
}

interface MyReportsPageProps {
  portalMode?: PortalMode;
}

const MyReportsPage: React.FC<MyReportsPageProps> = ({ portalMode = 'player' }) => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const paths = getPortalPaths(portalMode);
  const hideSidebar = portalMode === 'coach';
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [player, setPlayer] = useState<any>(null);
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [reportsLoaded, setReportsLoaded] = useState(false);
  const [filterSport, setFilterSport] = useState<string>('all');
  const [reportTab, setReportTab] = useState<'training' | 'ranking'>('training');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [recDialogOpen, setRecDialogOpen] = useState(false);
  const [recDialogRow, setRecDialogRow] = useState<ReportRow | null>(null);
  const [recLoading, setRecLoading] = useState(false);
  const [recError, setRecError] = useState<string | null>(null);
  const [recResult, setRecResult] = useState<{ combinedContent: string; perItem: Array<{ item: string; recommendation: string }> } | null>(null);
  const [reportIdsWithRec, setReportIdsWithRec] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadPlayerDetails();
  }, [user?.id]);

  useEffect(() => {
    if (player?.id) {
      setReportsLoaded(false);
      loadAllReports();
    } else {
      setReportsLoaded(false);
    }
  }, [player?.id]);

  useEffect(() => {
    if (!player?.id) return;
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    fetch(`/api/ai/recommendations/list?playerId=${player.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, cache: 'no-store' })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          setReportIdsWithRec(new Set(data.data.map((r: { reportId: string }) => String(r.reportId))));
        }
      })
      .catch(() => {});
  }, [player?.id]);

  const loadPlayerDetails = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/players/me?userId=${user.id}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await response.json();
      if (data.success && data.data) {
        setPlayer(data.data);
      } else {
        setError(data.message || 'Failed to load profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadAllReports = async () => {
    if (!player?.id) return;
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const all: ReportRow[] = [];

      const sports = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics'];
      for (const sport of sports) {
        const tr = await fetch(`/api/performance/basic/player/${player.id}?sport=${sport}`, { headers });
        const trData = await tr.json();
        if (trData.success && Array.isArray(trData.data)) {
          trData.data
            .filter((r: any) => r.submitted === true)
            .forEach((r: any) => {
              const at = r.assessmentType || 'high-level';
              all.push({
                id: r.id,
                type: 'training',
                sport,
                period: r.period || 'N/A',
                submittedAt: r.submittedAt || r.dateRange?.end || '',
                evaluatedBy: r.coachId ? 'Coach' : 'Self',
                scoreLabel: r.averageRating != null ? `Avg ${Number(r.averageRating).toFixed(1)}` : '–',
                reportId: r.id,
                assessmentType: at === 'quick' ? 'high-level' : at === 'advanced' ? 'low-level' : at,
              });
            });
        }

        const rr = await fetch(`/api/ranking-reports/player/${player.id}?sport=${sport}`, { headers });
        const rrData = await rr.json();
        if (rrData.success && Array.isArray(rrData.data)) {
          rrData.data.forEach((r: any) => {
            all.push({
              id: r.reportId,
              type: 'ranking',
              sport: r.sport || sport,
              period: r.periodKey || 'N/A',
              submittedAt: r.submittedAt || r.periodEndDate || '',
              evaluatedBy: r.submittedBy?.type === 'coach' ? 'Coach' : r.submittedBy?.type === 'admin' ? 'Admin' : 'Self',
              scoreLabel: r.grade ? `${r.grade} (${r.score ?? 0}/${r.maxScore ?? 0})` : `${r.score ?? 0} pts`,
              categoryLabel: CATEGORY_LABELS[r.categoryId] || r.categoryId,
              reportId: r.reportId,
            });
          });
        }
      }

      all.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
      setReports(all);
    } catch (err) {
      console.error('Load reports error:', err);
      setReports([]);
    } finally {
      setReportsLoaded(true);
    }
  };

  const trainingReports = reports.filter((r) => r.type === 'training' && (filterSport === 'all' || r.sport === filterSport));
  const rankingReports = reports.filter((r) => r.type === 'ranking' && (filterSport === 'all' || r.sport === filterSport));
  const filteredReports = reportTab === 'training' ? trainingReports : rankingReports;

  const handleView = (row: ReportRow) => {
    if (row.type === 'ranking' && row.reportId) {
      navigate(paths.rankingReport(row.reportId));
    } else if (row.type === 'training') {
      navigate(paths.performanceSubmittedReports(row.sport));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const refetchReportIdsWithRec = () => {
    if (!player?.id) return;
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    fetch(`/api/ai/recommendations/list?playerId=${player.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, cache: 'no-store' })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          setReportIdsWithRec(new Set(data.data.map((r: { reportId: string }) => String(r.reportId))));
        }
      })
      .catch(() => {});
  };

  const openRecommendationsDialog = (row: ReportRow) => {
    setRecDialogRow(row);
    setRecDialogOpen(true);
    setRecError(null);
    setRecResult(null);
    setRecLoading(true);
    if (!player?.id) {
      setRecLoading(false);
      return;
    }
    const reportId = String(row.reportId || row.id);
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    // Always try cached first (GET, no-store so browser never uses cached 404); only POST if no saved recommendation
    fetch(`/api/ai/recommendations?playerId=${player.id}&reportId=${encodeURIComponent(reportId)}`, {
      headers,
      cache: 'no-store',
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Not cached'))))
      .then((data) => {
        if (data.success && (data.combinedContent || data.perItem?.length)) {
          let combined = (data.combinedContent || '').trim();
          combined = toReadableRecommendations(combined);
          setRecResult({ combinedContent: combined, perItem: Array.isArray(data.perItem) ? data.perItem : [] });
          setRecLoading(false);
          return;
        }
        throw new Error('No cached');
      })
      .catch(() => {
        return fetch('/api/ai/recommendations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ playerId: player.id, reportId, reportType: row.type }),
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.success) {
              let combined = data.combinedContent || '';
              const perItemList = Array.isArray(data.perItem) ? data.perItem : [];
              if (!combined && (data.recommendations || data.trainingPlan)) {
                const rec = Array.isArray(data.recommendations) ? data.recommendations : [];
                const plan = data.trainingPlan || '';
                combined = (rec.length ? rec.map((r: string, i: number) => `${i + 1}. ${r}`).join('\n') + '\n\n' : '') + (plan ? `Practice / Plan:\n${plan}` : '');
              }
              combined = toReadableRecommendations(combined);
              setRecResult({ combinedContent: combined, perItem: perItemList });
              setReportIdsWithRec((prev) => new Set(prev).add(reportId));
              refetchReportIdsWithRec();
            } else {
              setRecError(data.message || 'Failed to get recommendations');
            }
          })
          .catch((e) => setRecError(e.message || 'Request failed'))
          .finally(() => setRecLoading(false));
      });
  };

  const closeRecommendationsDialog = () => {
    setRecDialogOpen(false);
    setRecDialogRow(null);
    setRecResult(null);
    setRecError(null);
  };

  if (loading && !player) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !player) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const drawer = <PlayerSidebar />;

  const pageContent = (
    <>
        <Container maxWidth="lg" sx={{ pt: hideSidebar ? 2 : 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 2,
              p: 3,
              borderRadius: 3,
              color: '#fff',
              background: 'radial-gradient(circle at 14% 20%, rgba(255,255,255,0.24), transparent 24%), linear-gradient(135deg, #172554 0%, #2563eb 50%, #7c3aed 100%)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ position: 'absolute', width: 170, height: 170, right: -50, top: -60, borderRadius: '50%', border: '28px solid rgba(255,255,255,0.14)' }} />
            <Box sx={{ position: 'relative' }}>
              <Typography variant="h4" gutterBottom fontWeight={950} sx={{ mt: 0, pt: 0 }}>
                Reports
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.86)' }}>
                All your submitted training and ranking evaluation reports.
              </Typography>
            </Box>
          </Box>

          <Paper variant="outlined" sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center', p: 1.5, borderRadius: 2.5, bgcolor: '#f8fafc', borderColor: '#dbe8f6' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Sport</InputLabel>
              <Select value={filterSport} label="Sport" onChange={(e) => setFilterSport(e.target.value)}>
                <MenuItem value="all">All Sports</MenuItem>
                <MenuItem value="Badminton">Badminton</MenuItem>
                <MenuItem value="Cricket">Cricket</MenuItem>
                <MenuItem value="Tennis">Tennis</MenuItem>
                <MenuItem value="Football">Football</MenuItem>
                <MenuItem value="Athletics">Athletics</MenuItem>
              </Select>
            </FormControl>
          </Paper>

          <Tabs
            value={reportTab}
            onChange={(_, v) => setReportTab(v)}
            TabIndicatorProps={{ sx: { display: 'none' } }}
            sx={{
              mb: 2,
              minHeight: 42,
              '& .MuiTabs-flexContainer': { gap: 1 },
              '& .MuiTab-root': {
                minHeight: 40,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 900,
                border: '1px solid #dbe8f6',
                bgcolor: '#fff',
              },
              '& .Mui-selected': {
                color: '#fff !important',
                background: 'linear-gradient(135deg, #1565c0, #6d28d9)',
              },
            }}
          >
            <Tab label={`Training (${trainingReports.length})`} value="training" />
            <Tab label={`Ranking (${rankingReports.length})`} value="ranking" />
          </Tabs>

          {!reportsLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredReports.length === 0 ? (
            <Alert severity="info">
              {reportTab === 'training' ? 'No submitted training reports.' : 'No submitted ranking reports.'}
              {filterSport !== 'all' ? ' Try "All Sports".' : ''}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', borderColor: '#dbe8f6', boxShadow: '0 12px 28px rgba(15, 76, 129, 0.08)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: reportTab === 'training' ? '#e3f2fd' : '#fff7ed' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Sport</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{reportTab === 'ranking' ? 'Category' : 'Period'}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Submitted</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Evaluated by</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Score / Grade</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReports.map((row) => (
                    <TableRow key={`${row.type}-${row.id}`} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fbfdff' } }}>
                      <TableCell>
                        <Chip size="small" label={row.sport} sx={{ fontWeight: 800, bgcolor: row.type === 'training' ? '#e3f2fd' : '#ffedd5', color: row.type === 'training' ? '#1565c0' : '#c2410c' }} />
                      </TableCell>
                      <TableCell>{reportTab === 'ranking' ? (row.categoryLabel || row.period) : row.period}</TableCell>
                      <TableCell>{row.submittedAt ? new Date(row.submittedAt).toLocaleDateString() : '–'}</TableCell>
                      <TableCell>{row.evaluatedBy}</TableCell>
                      <TableCell>{row.scoreLabel}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Button size="small" startIcon={<ViewIcon />} onClick={() => handleView(row)}>
                            View
                          </Button>
                          <Tooltip title={reportIdsWithRec.has(String(row.reportId || row.id)) ? 'View AI Assistant' : 'Get AI Assistant'}>
                            <IconButton
                              size="small"
                              onClick={() => openRecommendationsDialog(row)}
                              sx={{
                                color: reportIdsWithRec.has(String(row.reportId || row.id)) ? '#ff9800' : 'action.active',
                              }}
                              aria-label="AI Assistant"
                            >
                              <RecommendationsIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Container>

      <Dialog open={recDialogOpen} onClose={closeRecommendationsDialog} maxWidth="sm" fullWidth>
          <DialogTitle>AI Assistant</DialogTitle>
          <DialogContent dividers>
            {recDialogRow && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">Report</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {recDialogRow.sport} · {recDialogRow.type === 'ranking'
                    ? (recDialogRow.categoryLabel || 'Ranking')
                    : recDialogRow.assessmentType === 'low-level'
                      ? 'Skill Assessment Low-level'
                      : 'Skill Assessment High-level'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Period: {recDialogRow.period} · Score: {recDialogRow.scoreLabel} · Evaluated by: {recDialogRow.evaluatedBy}
                </Typography>
              </Paper>
            )}
            {recLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2">Loading recommendations…</Typography>
              </Box>
            )}
            {recError && (
              <Alert severity="error" onClose={() => setRecError(null)}>{recError}</Alert>
            )}
            {recResult && !recLoading && (
              <Box>
                {recResult.perItem && recResult.perItem.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>By item</Typography>
                    <Box component="ul" sx={{ pl: 2.5, mb: 2 }}>
                      {recResult.perItem.map((p, i) => (
                        <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
                          <strong>{p.item}:</strong> {p.recommendation}
                        </Typography>
                      ))}
                    </Box>
                  </>
                )}
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Recommendations &amp; practice</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {recResult.combinedContent && recResult.combinedContent.trim() ? recResult.combinedContent : 'No recommendations content returned. Try again or check that the report has data.'}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRecommendationsDialog}>Close</Button>
          </DialogActions>
        </Dialog>
    </>
  );

  if (hideSidebar) {
    return pageContent;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          display: { xs: 'block', md: 'none' },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
        open
      >
        {drawer}
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 2, pt: 1, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
        <Toolbar disableGutters sx={{ minHeight: 48 }}>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 2, display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
        {pageContent}
      </Box>
    </Box>
  );
};

export default MyReportsPage;
